import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useSession } from '@/auth/index';
import { UpdateEmailModal } from '@/components/index';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  Box,
  Button,
  Card,
  CustomIcons,
  Divider,
  Grid,
  Text,
  Icons,
  Link,
  Stack,
  styled,
  Modal,
} from '@/theme/index';
import { sendVerificationEmail } from '../EmailVerificationService';

const LinkButton = styled(Button)({
  textDecoration: 'underline',
  ':hover': { textDecoration: 'underline', backgroundColor: 'inherit' },
  padding: 0,
  lineHeight: 1.2,
  verticalAlign: 'baseline',
});

const EVENT_ACTION = 'engagement click';
const EVENT_LABEL = 'email verification blocker';

export function EmailVerificationBlocker({ open }: { open: boolean }) {
  const {
    images: { imgMailBox },
    links: { contactForm },
  } = useAppConstantsContext();
  const { trackEvent } = useAppConfigContext();
  const {
    session: { account },
  } = useSession();
  const [isUpdateEmailModalOpen, setIsUpdateEmailModalOpen] = useState(false);
  const [hasResentEmail, setHasResentEmail] = useState(false);
  const userEmail = account?.account.user_info?.email;

  const toggleUpdateEmailModal = () => {
    if (!isUpdateEmailModalOpen) {
      trackEvent('update email', 'navigation click', EVENT_LABEL);
    }
    setIsUpdateEmailModalOpen(!isUpdateEmailModalOpen);
  };

  const submitUpdateEmailModal = () => {
    trackEvent('update email', EVENT_ACTION, EVENT_LABEL);
    setIsUpdateEmailModalOpen(false);
  };

  const useSendVerificationEmail = useMutation(sendVerificationEmail);

  const handleResendingVerificationEmail = () => {
    trackEvent("can't find email", 'navigation click', EVENT_LABEL);
    useSendVerificationEmail.mutate();
    setHasResentEmail(true);
  };

  return (
    <Modal
      open={open}
      sx={{
        '.MuiDialogContent-root': {
          padding: 0,
        },
      }}
    >
      <Card
        sx={{
          padding: 2.5,
          border: 1,
          borderRadius: 2,
          maxWidth: 600,
          marginBottom: 0,
        }}
      >
        <Stack
          direction="column"
          textAlign="center"
          gap={1}
          justifyContent="space-between"
        >
          <Stack alignItems="center">
            <img
              src={imgMailBox}
              alt="Expired email link"
              style={{ height: '80px', width: '60px' }}
            />
          </Stack>

          <Text variant="h3" gutterBottom>
            Verify it's you
          </Text>

          {hasResentEmail ? (
            <>
              <Text
                variant="body1"
                align="center"
                gutterBottom
                sx={{ wordWrap: 'break-word' }}
              >
                We resent the email to <b>{userEmail || '...'}</b>
                <br />
                Please{' '}
                <Link
                  variant="h5"
                  onClick={() => {
                    trackEvent(
                      'contact customer care',
                      EVENT_ACTION,
                      EVENT_LABEL
                    );
                  }}
                  href={contactForm}
                  target="_blank"
                  rel="noopener"
                  color="primary.main"
                  underline="always"
                >
                  contact customer service
                </Link>{' '}
                if issue persists.
              </Text>
              <Stack alignItems="center">
                <Link
                  href="https://gmail.com"
                  target="_blank"
                  onClick={() => {
                    trackEvent('open gmail', EVENT_ACTION, EVENT_LABEL);
                  }}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      maxWidth: 174,
                      paddingY: 1,
                      whiteSpace: 'nowrap',
                    }}
                    color="success"
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CustomIcons.GmailIcon />
                      <Stack
                        direction="row"
                        spacing={0.8125}
                        alignItems="center"
                      >
                        <Text
                          variant="h5"
                          color="success"
                          sx={{ margin: 0, textDecoration: 'none' }}
                        >
                          Open Gmail
                        </Text>
                        <Icons.OpenInNewRounded color="primary" />
                      </Stack>
                    </Stack>
                  </Button>
                </Link>
              </Stack>
              <Text variant="body1" align="center" gutterBottom>
                If the above email address is incorrect, please{' '}
                <LinkButton variant="text" onClick={toggleUpdateEmailModal}>
                  update it here
                </LinkButton>
                .
              </Text>
            </>
          ) : (
            <>
              <Text variant="body1" align="center" gutterBottom>
                We sent a verification email to <b>{userEmail || '...'}</b>
                <br />
                Please click the link in your inbox to access your account.
              </Text>
              <Stack alignItems="center">
                <Link
                  href="https://gmail.com"
                  target="_blank"
                  onClick={() => {
                    trackEvent('open gmail', EVENT_ACTION, EVENT_LABEL);
                  }}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      maxWidth: 174,
                      paddingY: 1,
                      whiteSpace: 'nowrap',
                    }}
                    color="success"
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CustomIcons.GmailIcon />
                      <Stack
                        direction="row"
                        spacing={0.8125}
                        alignItems="center"
                      >
                        <Text
                          variant="h5"
                          color="success"
                          sx={{ margin: 0, textDecoration: 'none' }}
                        >
                          Open Gmail
                        </Text>
                        <Icons.OpenInNewRounded color="primary" />
                      </Stack>
                    </Stack>
                  </Button>
                </Link>
              </Stack>
              <Text variant="body1" align="center" gutterBottom>
                If the above email address is incorrect, please{' '}
                <LinkButton variant="text" onClick={toggleUpdateEmailModal}>
                  update it here
                </LinkButton>
                .
              </Text>

              <Button
                variant="outlined"
                sx={{
                  maxWidth: 174,
                  paddingY: 1,
                  whiteSpace: 'nowrap',
                  alignSelf: 'center',
                }}
                color="success"
                onClick={handleResendingVerificationEmail}
              >
                <Text
                  variant="h5"
                  color="success"
                  sx={{ margin: 0, textDecoration: 'none' }}
                >
                  Can't find the email?
                </Text>
              </Button>
              <Divider sx={{ marginY: 2 }} />

              <Box>
                <Text variant="h3">Why Verify?</Text>
                <Grid
                  container
                  direction={{ xs: 'column', sm: 'row' }}
                  paddingX={{ xs: 2.5 }}
                  flexWrap="nowrap"
                  gap={1}
                >
                  <Grid
                    item
                    container
                    direction="column"
                    alignItems="center"
                    gap={1}
                  >
                    <Icons.Help color="primary" />
                    <Text>Speed up password recovery</Text>
                  </Grid>
                  <Grid
                    item
                    container
                    direction="column"
                    alignItems="center"
                    gap={1}
                  >
                    <Icons.Lock color="primary" />
                    <Text>Secure your account</Text>
                  </Grid>
                  <Grid
                    item
                    container
                    direction="column"
                    alignItems="center"
                    gap={1}
                  >
                    <Icons.Email color="primary" />
                    <Text>Ensure important updates reach your inbox</Text>
                  </Grid>
                  <Grid
                    item
                    container
                    direction="column"
                    alignItems="center"
                    gap={1}
                  >
                    <Icons.WatchLater color="primary" />
                    <Text>It takes less than 30 seconds!</Text>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </Stack>
      </Card>
      <UpdateEmailModal
        email={userEmail || ''}
        isOpen={isUpdateEmailModalOpen}
        onSubmit={submitUpdateEmailModal}
        onCloseHandle={toggleUpdateEmailModal}
      />
    </Modal>
  );
}
